/**
 * «Por hacer»: la cola de trabajo del ciclo de cobro.
 *
 * La matriz responde *qué estado tiene cada mes*; esto responde *qué tengo que
 * hacer hoy*. Cuatro acciones, en el orden en que el negocio las vive:
 * cobrar → definir con quién se reparte → facturar → revisar.
 *
 * La deuda se muestra **por consultorio**, porque el cliente es el consultorio:
 * un mes sin reparto definido igual se debe, y tiene que verse. El pagador
 * aparece cuando el reparto ya lo define, y en blanco cuando todavía es una
 * conversación pendiente con el consultorio.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { periodLabelShort } from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { Async, Badge, Button, Card, Empty, Money, Note, Table } from '@/components/ui';

type ActionKind = 'COBRAR' | 'DEFINIR_REPARTO' | 'FACTURAR' | 'REVISAR';

interface ActionItem {
  kind: ActionKind;
  unit_id: string; unit_code: string; unit_name: string | null;
  party_id: string | null; party_name: string | null;
  periods: string[];
  amount: string;
  oldest_period: string;
  note: string | null;
}

interface ActionsData {
  items: ActionItem[];
  totals: { cobrar: string; definir: string; facturar_count: number; revisar_count: number };
}

const KIND_META: Record<ActionKind, {
  label: string; verb: string; tone: 'danger' | 'warn' | 'info' | 'neutral'; hint: string;
}> = {
  COBRAR: {
    label: 'Cobrar', verb: 'Cobrar',
    tone: 'danger',
    hint: 'El reparto ya define quién paga y el mes sigue sin pagarse.',
  },
  DEFINIR_REPARTO: {
    label: 'Definir quién paga', verb: 'Acordar reparto',
    tone: 'warn',
    hint: 'El consultorio debe estos meses, pero nadie los tiene asignados. Hay que acordarlo con ellos.',
  },
  FACTURAR: {
    label: 'Facturar', verb: 'Emitir factura',
    tone: 'info',
    hint: 'Ya pagaron y falta emitir la factura fiscal.',
  },
  REVISAR: {
    label: 'Revisar', verb: 'Revisar',
    tone: 'neutral',
    hint: 'El histórico no alcanza para afirmar qué pasó ese mes.',
  },
};

const ORDER: ActionKind[] = ['COBRAR', 'DEFINIR_REPARTO', 'FACTURAR', 'REVISAR'];

/** Los meses como lista corta y legible: «oct 25 → ago 26 (11 meses)». */
function periodRange(periods: string[]): string {
  if (periods.length === 0) return '—';
  if (periods.length === 1) return periodLabelShort(periods[0]!);
  if (periods.length <= 3) return periods.map(periodLabelShort).join(', ');
  return `${periodLabelShort(periods[0]!)} → ${periodLabelShort(periods[periods.length - 1]!)} (${periods.length} meses)`;
}

interface OverdueData {
  overdue: { charge_payer_id: string; phone: string | null; email: string | null }[];
  without_contact: number;
}

export function ActionsPage() {
  const query = useApi<ActionsData>('/admin/actions');
  const overdue = useApi<OverdueData>('/admin/receivables/overdue');
  const [filter, setFilter] = useState<ActionKind | 'TODO'>('TODO');
  const [result, setResult] = useState<{ sent: number; skipped: number; without_contact: number } | null>(null);

  /**
   * Le avisa a todos los que deben, de una. Clean Blue no manda el mensaje:
   * emite la señal con el contacto y el contexto, y el envío lo hace Rosetta.
   */
  const remindAll = useApiMutation(
    () => api.post<{ sent: number; skipped: number; without_contact: number }>(
      '/admin/receivables/remind-overdue'),
    ['/admin/actions', '/admin/receivables'],
    { onSuccess: (r) => setResult(r) },
  );

  const pendingCount = overdue.data?.overdue.length ?? 0;
  const noContact = overdue.data?.without_contact ?? 0;

  return (
    <Async query={query}>
      {(data) => {
        const shown = filter === 'TODO' ? data.items : data.items.filter((i) => i.kind === filter);
        const countOf = (k: ActionKind): number => data.items.filter((i) => i.kind === k).length;

        return (
          <>
            <div className="grid grid--4 mb-2">
              <Card title="Por cobrar">
                <Money value={data.totals.cobrar} size="lg" />
                <p className="small muted mt-1">{countOf('COBRAR')} pagadores con meses pendientes</p>
              </Card>
              <Card title="Sin reparto definido">
                <Money value={data.totals.definir} size="lg" />
                <p className="small muted mt-1">{countOf('DEFINIR_REPARTO')} consultorios por acordar</p>
              </Card>
              <Card title="Por facturar">
                <span className="stat__value">{data.totals.facturar_count}</span>
                <p className="small muted mt-1">meses pagados sin factura fiscal</p>
              </Card>
              <Card title="Por revisar">
                <span className="stat__value">{data.totals.revisar_count}</span>
                <p className="small muted mt-1">meses sin evidencia suficiente</p>
              </Card>
            </div>

            {pendingCount > 0 && (
              <Card>
                <div className="row row--wrap" style={{ justifyContent: 'space-between', gap: 10 }}>
                  <div>
                    <strong>{pendingCount} cobros vencidos</strong>
                    <p className="small muted" style={{ margin: '4px 0 0' }}>
                      Se le avisa a cada uno por WhatsApp si tiene teléfono, o por correo si no.
                      El aviso también sale solo todos los días.
                      {noContact > 0 && ` ${noContact} sin contacto cargado quedan afuera.`}
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    disabled={remindAll.isPending}
                    onClick={() => remindAll.mutate()}
                  >
                    {remindAll.isPending ? 'Avisando…' : 'Recordarles a todos'}
                  </Button>
                </div>
                {result && (
                  <Note tone={result.sent > 0 ? 'ok' : 'info'}>
                    {result.sent > 0
                      ? `Se avisó a ${result.sent}.`
                      : 'No había a quién avisarle todavía.'}
                    {result.skipped > 0 && ` ${result.skipped} ya tenían aviso de hoy.`}
                    {result.without_contact > 0 && ` ${result.without_contact} sin teléfono ni correo.`}
                  </Note>
                )}
                {remindAll.error && <Note tone="danger">{remindAll.error.message}</Note>}
              </Card>
            )}

            <div className="row row--wrap mb-2 mt-2" style={{ gap: 6 }}>
              <Button variant={filter === 'TODO' ? 'primary' : undefined} onClick={() => setFilter('TODO')}>
                Todo ({data.items.length})
              </Button>
              {ORDER.map((k) => (
                <Button key={k} variant={filter === k ? 'primary' : undefined} onClick={() => setFilter(k)}>
                  {KIND_META[k].label} ({countOf(k)})
                </Button>
              ))}
            </div>

            {filter !== 'TODO' && <p className="small muted mb-2">{KIND_META[filter].hint}</p>}

            <Card flush>
              {shown.length === 0 ? (
                <Empty icon="confirmar" title="Nada pendiente acá" hint="Cuando aparezca algo por cobrar o facturar, se lista en esta pantalla." />
              ) : (
                <Table head={
                  <tr>
                    <th>Acción</th><th>Consultorio</th><th>A quién</th><th>Meses</th>
                    <th className="right">Monto</th><th />
                  </tr>
                }>
                  {shown.map((item, i) => {
                    const meta = KIND_META[item.kind];
                    return (
                      <tr key={`${item.kind}-${item.unit_id}-${item.party_id ?? 'x'}-${i}`}>
                        <td><Badge tone={meta.tone}>{meta.label}</Badge></td>
                        <td className="strong">
                          <Link to={`/admin/consultorios/${item.unit_id}`}>{item.unit_code}</Link>
                        </td>
                        <td>
                          {item.party_name ?? (
                            <span className="muted small">Sin asignar — acordar con el consultorio</span>
                          )}
                        </td>
                        <td className="small">
                          {periodRange(item.periods)}
                          {item.note && <div className="tbl__sub">{item.note}</div>}
                        </td>
                        <td className="right"><Money value={item.amount} /></td>
                        <td className="right">
                          <Link
                            to={item.kind === 'DEFINIR_REPARTO'
                              ? `/admin/consultorios/${item.unit_id}`
                              : `/admin/cobros?unit=${item.unit_code}`}
                            className="btn"
                          >
                            {meta.verb}
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </Table>
              )}
            </Card>
          </>
        );
      }}
    </Async>
  );
}
